import { Avatar, Box, Button, CircularProgress, IconButton } from '@material-ui/core';
import { getMerchantStructureByMerchantId, MerchantStructure } from '@nemuru-eng/js-api-client';
import { generatePath, useParams } from 'react-router-dom';

import { Font } from '@/components/shared/components/Font';
import { SeverityLevel } from '@/components/shared/components/NotificationSnackbar/types/SeverityLevel';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { NewAgentCloned } from '@/components/widgets/AgentDetailActions/components/CloneAgent/components/Agents/types/NewAgentCloned';
import { StatusTagType } from '@/config/types';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';
import MerchantStructureEntity from '@/entities/MerchantStructureEntity';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface ProcessingAgentCardProps {
  agent: NewAgentCloned;
  merchantStructure: MerchantStructure;
}

export const ProcessingAgentCard = ({ agent, merchantStructure }: ProcessingAgentCardProps) => {
  const { palette, customBorderRadius } = useCustomTheme();
  const { merchantId } = useParams();
  const {
    actions: { setSnackbarNotification },
  } = useNotificationsContext();

  const onRedirect = async () => {
    if (!merchantId) return;
    try {
      const newMerchantStructure = await getMerchantStructureByMerchantId({
        merchantId: merchantStructure.merchant.id,
      });

      const agentId = agent.isCloned ? agent.agentId : agent.clonedAgentId;

      const channel = MerchantStructureEntity.create({
        merchantStructure: newMerchantStructure,
        agentId,
      }).getChannel();

      if (!channel) throw new Error('Channel not found');

      const url = generatePath(DYNAMIC_PAGE_PATHS.AGENT, {
        merchantId,
        channelId: channel.id,
        agentId,
      });
      window.open(url, '_blank');
    } catch (error) {
      setSnackbarNotification({ error, level: SeverityLevel.ERROR });
    }
  };

  return (
    <Box py={1} px={2} bgcolor={palette.grey[50]} borderRadius={customBorderRadius.paper}>
      <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} my={1}>
        <Box display={'flex'} alignItems={'center'}>
          <Box width={'24px'} height={'24px'}>
            {agent.isSubmitting && <CircularProgress size={24} />}
            {agent.hasError && (
              <Avatar
                style={{
                  background: palette.error.main,
                  width: 24,
                  height: 24,
                  color: palette.error.contrastText,
                }}
              >
                <i className={'material-icons-outlined'} style={{ fontSize: 18 }}>
                  close
                </i>
              </Avatar>
            )}
            {agent.isCloned && (
              <Avatar
                style={{
                  background: palette.success.main,
                  width: 24,
                  height: 24,
                  color: palette.success.contrastText,
                }}
              >
                <i className={'material-icons-outlined'} style={{ fontSize: 18 }}>
                  check
                </i>
              </Avatar>
            )}
            {agent.isUpdated && (
              <Avatar
                style={{
                  background: palette.warning.main,
                  width: 24,
                  height: 24,
                  color: palette.warning.contrastText,
                }}
              >
                <i className={'material-icons-outlined'} style={{ fontSize: 18 }}>
                  priority_high
                </i>
              </Avatar>
            )}
          </Box>
          <Box ml={2}>
            <Font variant={'body2'} color={'textPrimary'} style={{ display: 'block' }}>
              <strong>{agent.agentName}</strong>
            </Font>
            {agent.agentEmail && (
              <Font variant={'body2'} color={'textPrimary'}>
                {agent.agentEmail}
              </Font>
            )}
          </Box>
        </Box>
        <Box>
          {agent.isCloned && <StatusTag type={StatusTagType.SUCCESS} labelTextId={'CLONE_SUCCESS'} />}
          {agent.isUpdated && <StatusTag type={StatusTagType.WARNING} labelTextId={'CLONE_UPDATED'} />}
          {agent.hasError && (
            <>
              <Button onClick={() => window.alert(agent.errorMessage)}>
                <StatusTag type={StatusTagType.ERROR} labelTextId={'CLONE_ERROR'} />
              </Button>
            </>
          )}
          <IconButton
            size={'small'}
            className={'ml-2'}
            onClick={onRedirect}
            disabled={!(agent.isCloned || agent.isUpdated)}
          >
            <i className={'material-icons-outlined'}>open_in_new</i>
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
